'use client'

import * as React from 'react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { Button } from '@repo/design-system-cf/components/button'
import { GenerateButton } from '@repo/design-system-cf/components/generate-button'
import { Typography } from '@repo/design-system-cf/components/typography'
import { BaseCard } from '@repo/design-system-cf/components/base-card'
import { IconUpload } from '@repo/design-system-cf/components/icons'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { LFS_URL } from '@web/utils/env'
import { Surface } from '@repo/rpc-types-creativefabrica-common-v1/surface/v1'
import { useUploadImage } from '@repo/studio-components-image-processing/use-upload-image'
import {
  ListQueryRootKey,
  initiateFn,
  useGetPricingQuery,
} from '@web/gen/service/creativefabrica/backgroundremover/v2'
import { useAllAccessGuard } from '@web/hooks/use-all-access-guard'
import { useMarketplaceToolError } from '../../_components/marketplace-tool-errors'
import { clearPersistedFile, usePersistedFile } from '../../_components/persisted-form-state'
import {
  rememberGeneratingPreview,
  usePreviewThumbnailRef,
} from '../../_components/generating-preview'
import { PricingAction } from '@repo/studio-hooks-coins/enums'
import { MarketplaceAutoTopUpNotice } from '../../_components/marketplace-auto-top-up-notice'
import { useSingleSubmit } from '../../_components/use-single-submit'
import { ToolFileDrop } from '../../_components/tool-file-drop'
import { ToolHomeLayout } from '../../_components/tool-home-layout'
import { ACCEPTED_IMAGE_EXTENSIONS, isAcceptedBackgroundRemoverFile } from './file-accept'

const FILE_STORAGE_KEY = 'background-remover'

const EXAMPLE_IDS = ['daniel', 'autumn', 'coffee'] as const

export function BackgroundRemoverTool() {
  return <Home />
}

function Home() {
  const home = useBackgroundRemoverHome()
  const t = useTranslations('pages.background-remover')

  return (
    <ToolFileDrop
      activeLabel={t('drop_image')}
      disabled={home.isSubmitting}
      onFile={home.acceptFile}
    >
      <ToolHomeLayout>
        <HomeIntro />
        <HomeUpload home={home} />
      </ToolHomeLayout>
    </ToolFileDrop>
  )
}

function useBackgroundRemoverHome() {
  const t = useTranslations('pages.background-remover')
  const locale = useLocale()
  const router = useRouter()
  const guard = useAllAccessGuard()
  const handleToolError = useMarketplaceToolError()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [file, setFile] = usePersistedFile(FILE_STORAGE_KEY)
  const [errorKey, setErrorKey] = React.useState<'invalid_type' | null>(null)
  const thumbnail = usePreviewThumbnailRef(file)
  const { data: price } = useGetPricingQuery(
    { imageCount: 1 },
    {
      select: data => Number(data.unitAmount),
    },
  )

  const { uploadMutation } = useUploadImage({
    telemetryTool: 'Background Remover',
    initiateMutationFn: variables =>
      initiateFn({
        ...variables,
        surface: Surface.MARKETPLACE,
      }),
    listQueryKey: ListQueryRootKey,
    options: {
      onSessionCreated(sessionId) {
        rememberGeneratingPreview(sessionId, thumbnail.current)
        void clearPersistedFile(FILE_STORAGE_KEY)
        router.push(
          addOrReplaceLocaleInUrl(`${AppRoutes.TOOLS_BACKGROUND_REMOVER}${sessionId}`, locale),
        )
      },
      onError(error) {
        handleToolError(error, t('failed'))
      },
    },
  })

  const { isSubmitting, submit } = useSingleSubmit(uploadMutation)

  async function startUpload(next: File) {
    const allowed = await guard()
    if (!allowed) {
      return
    }
    uploadMutation.mutate({ files: [next] })
  }

  function acceptFile(nextFile: File) {
    if (!isAcceptedBackgroundRemoverFile(nextFile)) {
      setFile(null)
      setErrorKey('invalid_type')
      return
    }
    setErrorKey(null)
    setFile(nextFile)
    submit(() => startUpload(nextFile))
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const nextFile = event.target.files?.[0]
    event.target.value = ''
    if (!nextFile) {
      return
    }
    acceptFile(nextFile)
  }

  function handleSubmit() {
    if (!file) {
      inputRef.current?.click()
      return
    }
    submit(() => startUpload(file))
  }

  return {
    file,
    errorKey,
    price,
    inputRef,
    isSubmitting,
    acceptFile,
    handleFileChange,
    handleSubmit,
  }
}

function HomeIntro() {
  const t = useTranslations('pages.background-remover')
  return (
    <header className="flex w-full max-w-96 flex-col items-center gap-2 text-center lg:items-start lg:text-left">
      <Typography variant="cf-label-01">{t('kicker')}</Typography>
      <Typography variant="cf-title-07" asChild>
        <h1>{t('title')}</h1>
      </Typography>
      <Typography variant="body-01">{t('description')}</Typography>
    </header>
  )
}

function HomeUpload({ home }: { home: ReturnType<typeof useBackgroundRemoverHome> }) {
  const t = useTranslations('pages.background-remover')
  return (
    <section className="flex w-full min-w-0 max-w-lg flex-col gap-3">
      <BaseCard size="md">
        <div className="flex min-w-0 flex-col gap-4">
          <input
            ref={home.inputRef}
            type="file"
            accept={ACCEPTED_IMAGE_EXTENSIONS}
            className="sr-only"
            onChange={home.handleFileChange}
          />
          {home.file ? (
            <Button
              variant="secondary"
              fullWidth
              title={home.file.name}
              className="min-w-0 max-w-full overflow-hidden"
              textClassName="min-w-0 truncate"
              iconLeft={<IconUpload className="h-5 w-5" />}
              onClick={() => home.inputRef.current?.click()}
              disabled={home.isSubmitting}
            >
              {home.file.name}
            </Button>
          ) : null}
          <GenerateButton
            loading={home.isSubmitting}
            loadingText={t('uploading')}
            disabled={home.isSubmitting}
            onClick={home.handleSubmit}
            price={home.price}
          >
            {t('remove_background')}
          </GenerateButton>
          <MarketplaceAutoTopUpNotice pricingAction={PricingAction.REMOVE_BACKGROUND} />
          <Typography variant="cf-label-01">{t('upload_hint')}</Typography>
          {home.errorKey ? (
            <Typography variant="body-compact-01" className="text-cf-red-800">
              {t(home.errorKey)}
            </Typography>
          ) : null}
        </div>
      </BaseCard>
      <HomeExamples />
    </section>
  )
}

function HomeExamples() {
  const t = useTranslations('pages.background-remover')
  return (
    <section className="flex items-center gap-3">
      <header className="flex flex-col">
        <Typography variant="cf-label-02-B">{t('examples_title')}</Typography>
        <Typography variant="cf-label-01">{t('examples_description')}</Typography>
      </header>
      <ul className="flex gap-2">
        {EXAMPLE_IDS.map(id => (
          <li key={id}>
            <Link
              href={`${AppRoutes.TOOLS_BACKGROUND_REMOVER}${id}`}
              component="Link"
              className="block size-10 overflow-hidden rounded-2"
            >
              <Image
                src={`${LFS_URL}/studio/background-remover/examples/${id}_thumb.webp`}
                alt=""
                width={40}
                height={40}
                className="size-full object-cover"
              />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}
